import React from 'react';
import PropTypes from 'prop-types';

import {Box, ButtonBase, IconButton, Stack, Tooltip, Typography} from 'views/components';
import {ChevronLeft, ChevronRight} from 'views/icons';
import {Translate, translate} from 'views/module';

import ClientContext from './ClientContext';

import backend from 'utils/backend';
import userUtil from 'utils/miscellaneous/user/user_util';

const ResourceTile = (props) => {
  const {title, subtitle, path} = props;

  const openResourceLink = (linkPath) => {
    window.open(`${window.location.origin}${linkPath}`, '_blank', 'noopener');
  };

  return (
    <ButtonBase
      onClick={() => openResourceLink(path)}
      sx={{
        'display': 'block',
        'width': '100%',
        'textAlign': 'left',
        'border': '1px solid #ECECF1',
        'borderRadius': '10px',
        'backgroundColor': '#FFFFFF',
        'p': '13px 15px',
        '&:hover': {
          backgroundColor: '#F7F8FA',
          borderColor: '#DCDDE5',
        },
      }}
      type='button'
    >
      <Stack sx={{gap: 0.75}}>
        <Typography sx={{fontSize: '13px', fontWeight: 700, color: '#050A14', wordBreak: 'break-word'}}>
          {title}
        </Typography>
        {
          subtitle && (
            <Typography sx={{fontSize: '12px', fontWeight: 400, color: '#63708A', wordBreak: 'break-word'}}>
              {subtitle}
            </Typography>
          )
        }
      </Stack>
    </ButtonBase>
  );
};

ResourceTile.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  path: PropTypes.string.isRequired,
};

const ChannelDetail = (props) => {
  const {channel, isExpanded, toggleExpanded} = props;

  const [client, setClient] = React.useState(null);
  const [clientStage, setClientStage] = React.useState('FILL');

  const clientRelationshipMarker = ':RELATIONSHIP-';

  const resolveResourceLink = (rn) => {
    if (typeof rn !== 'string') {
      return null;
    }

    const relationshipMarkerIndex = rn.indexOf(clientRelationshipMarker);
    const isRelationship = relationshipMarkerIndex !== -1;
    const resourceRn = isRelationship ? rn.slice(0, relationshipMarkerIndex) : rn;

    const resourceLinkByRnPrefix = [
      {prefix: 'RN:CLIENT-V1-', kind: 'CLIENT', buildPath: (value) => `/client/${value}`},
      {prefix: 'RN:CAREGIVER-V1-', kind: 'CAREGIVER', buildPath: (value) => `/caregiver/${value}`},
      {prefix: 'RN:CASE-V1-', kind: 'CASE', buildPath: (value) => `/case/${value}`},
    ];

    const match = resourceLinkByRnPrefix.find((entry) => resourceRn.startsWith(entry.prefix));

    if (!match) {
      return null;
    }

    return {
      kind: isRelationship && match.kind === 'CLIENT' ? 'RELATIONSHIP' : match.kind,
      rn: resourceRn,
      path: match.buildPath(resourceRn),
    };
  };

  const channelTypeLabel = channel ?
    translate(`inbox.List.ChannelType.Options.${channel.channelType}`) :
    null;
  const contextLink = channel ? resolveResourceLink(channel.contextRn) : null;
  const participantTiles = (channel?.participants || [])
      .map((participant) => ({
        userRn: participant.userRn,
        name: userUtil.getFullName(participant.firstName, participant.lastName).trim() || participant.userRn,
        link: resolveResourceLink(participant.userRn),
      }))
      .filter((entry) => entry.link);
  const clientRn = [contextLink, ...participantTiles.map((entry) => entry.link)]
      .find((link) => link && (link.kind === 'CLIENT' || link.kind === 'RELATIONSHIP'))?.rn;

  React.useEffect(() => {
    if (!clientRn) {
      setClient(null);
      setClientStage('FILL');
      return;
    }

    setClientStage('LOADING');

    (async () => {
      try {
        const output = await backend.client.getClient(clientRn, false, false, true);

        setClient(output);
        setClientStage('FILL');
      } catch (error) {
        console.error('Failed to load the channel client:', error);

        setClient(null);
        setClientStage('ERROR');
      }
    })();
  }, [clientRn]);

  const relationshipsByRn = client?.clientDetail?.relationships || {};

  const collapseToggle = (
    <Tooltip title={translate(isExpanded ? 'inbox.Detail.Collapse' : 'inbox.Detail.Expand')}>
      <IconButton
        aria-label={translate(isExpanded ? 'inbox.Detail.Collapse' : 'inbox.Detail.Expand')}
        onClick={toggleExpanded}
        size='small'
        sx={{
          'flexShrink': 0,
          'color': '#63708A',
          '&:hover': {backgroundColor: '#F7F8FA'},
        }}
      >
        {
          isExpanded ?
            <ChevronRight sx={{fontSize: 20}} /> :
            <ChevronLeft sx={{fontSize: 20}} />
        }
      </IconButton>
    </Tooltip>
  );

  if (!isExpanded) {
    return (
      <Box sx={{display: 'flex', justifyContent: 'center', p: '14px 8px', backgroundColor: '#FFFFFF'}}>
        {collapseToggle}
      </Box>
    );
  }

  if (!channel) {
    return (
      <Box sx={{p: 2, backgroundColor: '#FFFFFF'}}>
        <Stack direction='row' sx={{alignItems: 'flex-start', justifyContent: 'space-between', gap: 1}}>
          <Typography sx={{fontSize: '14px', fontWeight: 400, color: '#6D7280'}}>
            <Translate expression='inbox.Detail.NoChannelSelected' />
          </Typography>
          {collapseToggle}
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{p: '22px 18px 16px 21px', backgroundColor: '#FFFFFF'}}>
      <Stack gap={2}>
        <Stack direction='row' sx={{alignItems: 'flex-start', justifyContent: 'space-between', gap: 1}}>
          <Typography
            sx={{
              fontSize: '28px',
              fontWeight: 700,
              lineHeight: '34px',
              color: '#050A14',
            }}
          >
            {channelTypeLabel}
          </Typography>
          {collapseToggle}
        </Stack>

        {
          contextLink && (
            <Stack sx={{gap: 1}}>
              <ResourceTile
                title={translate(`inbox.Detail.ResourceKinds.${contextLink.kind}`)}
                path={contextLink.path}
              />
            </Stack>
          )
        }

        {
          clientRn &&
            <ClientContext client={client} stage={clientStage} />
        }

        <Stack sx={{gap: 1}}>
          <Typography sx={{fontWeight: 500, fontSize: '11px', lineHeight: '14px', color: '#9A9BAE'}}>
            <Translate expression='inbox.Detail.Fields.Participants' />
          </Typography>
          {
            participantTiles.length === 0 &&
              <Typography sx={{fontSize: '12px', fontWeight: 400, color: '#6D7280'}}>
                <Translate expression='inbox.Detail.NoParticipants' />
              </Typography>
          }
          {
            participantTiles.map((entry) => (
              <ResourceTile
                key={entry.userRn}
                title={entry.name}
                subtitle={
                  [
                    translate(`inbox.Detail.ResourceKinds.${entry.link.kind}`),
                    relationshipsByRn[entry.userRn]?.connectionType,
                  ].filter(Boolean).join(' · ')
                }
                path={entry.link.path}
              />
            ))
          }
        </Stack>
      </Stack>
    </Box>
  );
};

ChannelDetail.propTypes = {
  channel: PropTypes.shape({
    channelType: PropTypes.string,
    contextRn: PropTypes.string,
    conversationRn: PropTypes.string,
    participants: PropTypes.array,
  }),
  isExpanded: PropTypes.bool.isRequired,
  toggleExpanded: PropTypes.func.isRequired,
};

export default React.memo(ChannelDetail);
